import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import type { Occasion, SetupSuggestions, StoryLanguage, StoryLength, StoryMood, StorySetup } from '../../types';
import { ActionBar, ChipQuestion, StepHeader, primaryBtn, secondaryBtn } from './ui';

export const OCCASIONS: Array<{ value: Occasion; label: string }> = [
  { value: 'MEHNDI', label: 'Mehndi' },
  { value: 'BARAAT', label: 'Baraat' },
  { value: 'WALIMA', label: 'Walima' },
  { value: 'BIRTHDAY', label: 'Birthday' },
  { value: 'TRIP', label: 'Trip' },
  { value: 'EID', label: 'Eid' },
  { value: 'OTHER', label: 'Something else' },
];
export const STORY_MOODS: Array<{ value: StoryMood; label: string }> = [
  { value: 'JOYFUL', label: 'Joyful' },
  { value: 'FUNNY', label: 'Funny' },
  { value: 'SARCASTIC', label: 'Playfully sarcastic' },
  { value: 'EMOTIONAL', label: 'Emotional' },
  { value: 'NOSTALGIC', label: 'Nostalgic' },
  { value: 'CALM', label: 'Calm' },
  { value: 'ADVENTUROUS', label: 'Adventurous' },
];
export const LANGUAGES: Array<{ value: StoryLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ur', label: 'اردو Urdu' },
  { value: 'both', label: 'Both' },
];
export const LENGTHS: Array<{ value: StoryLength; label: string; sub: string }> = [
  { value: 'short', label: 'Short', sub: '~1 min' },
  { value: 'medium', label: 'Medium', sub: '~2–3 min' },
  { value: 'full', label: 'Full', sub: 'every memory' },
];

/** Both languages are always written; the answer picks the one shown first (Both: Urdu). */
export function storyLanguageFor(language: StoryLanguage | null): 'en' | 'ur' | null {
  return language === 'en' ? 'en' : language ? 'ur' : null;
}

export interface StoryAnswers {
  occasion: Occasion | null;
  occasionOther: string;
  about: string;
  mood: StoryMood | null;
  language: StoryLanguage | null;
  length: StoryLength | null;
}

/** Saved answers, prefilled. Language falls back to the older first-language field. */
export function answersOf(setup: StorySetup): StoryAnswers {
  return {
    occasion: setup.occasion,
    occasionOther: setup.occasionOther ?? '',
    about: setup.about ?? '',
    mood: setup.mood,
    language: setup.language ?? (setup.storyLanguage === 'en' ? 'en' : setup.storyLanguage ? 'ur' : null),
    length: setup.length,
  };
}

/** The answers as a patch for api.saveEpisodeSetup. */
export function answersPatch(a: StoryAnswers) {
  return {
    occasion: a.occasion,
    occasionOther: a.occasion === 'OTHER' ? a.occasionOther : null,
    about: a.about,
    mood: a.mood,
    language: a.language,
    ...(a.language ? { storyLanguage: storyLanguageFor(a.language) } : {}),
    length: a.length,
  };
}

/**
 * Occasion, "Tell us about it", mood, language and length: asked in the
 * create wizard and again in the story panel on the episode page.
 * `onCommit` runs when typing is finished (the field loses focus).
 */
export function StoryAnswerFields({
  value,
  onChange,
  onCommit,
  moodLabel = 'What mood should it have?',
  idPrefix = 'q',
}: {
  value: StoryAnswers;
  onChange: (patch: Partial<StoryAnswers>) => void;
  onCommit?: () => void;
  moodLabel?: string;
  idPrefix?: string;
}) {
  const { occasion, occasionOther, about, mood, language, length } = value;
  const wedding = occasion === 'MEHNDI' || occasion === 'BARAAT' || occasion === 'WALIMA';
  return (
    <>
      <ChipQuestion label="What was the occasion?" options={OCCASIONS} value={occasion} onChange={(v) => onChange({ occasion: v })} />
      {wedding && (
        <p className="-mt-5 mb-7 text-xs text-white/45">
          Each wedding function is its own episode, so Mehndi, Baraat and Walima stay separate.
        </p>
      )}
      {occasion === 'OTHER' && (
        <label className="-mt-4 mb-7 block">
          <span className="sr-only">Name the occasion</span>
          <input
            value={occasionOther}
            onChange={(e) => onChange({ occasionOther: e.target.value })}
            onBlur={onCommit}
            maxLength={60}
            placeholder="e.g. Aqiqah, graduation, family dinner"
            className="focus-ring w-full rounded-lg bg-white/[0.07] px-4 py-3 text-base ring-1 ring-white/10"
          />
        </label>
      )}

      <div className="mb-7">
        <label htmlFor={`${idPrefix}-about`} className="mb-1 block text-sm font-semibold">
          Tell us about it
        </label>
        <p className="mb-2 text-xs text-white/45">
          A line or two: who was there, what happened, how you want it remembered.
        </p>
        <textarea
          id={`${idPrefix}-about`}
          value={about}
          onChange={(e) => onChange({ about: e.target.value })}
          onBlur={onCommit}
          maxLength={1000}
          rows={4}
          className="focus-ring w-full rounded-lg bg-white/[0.07] px-4 py-3 text-base ring-1 ring-white/10"
        />
        <Dictation
          lang={language === 'ur' ? 'ur-PK' : 'en-US'}
          onText={(t) => onChange({ about: about ? `${about.trimEnd()} ${t}` : t })}
          onStop={onCommit}
        />
      </div>

      <ChipQuestion label={moodLabel} options={STORY_MOODS} value={mood} onChange={(v) => onChange({ mood: v })} />
      <ChipQuestion label="Language for the story" options={LANGUAGES} value={language} onChange={(v) => onChange({ language: v })} />
      <ChipQuestion label="How long?" options={LENGTHS} value={length} onChange={(v) => onChange({ length: v })} />
    </>
  );
}

/** Step 4: a few quick questions. Every answer is optional. */
export default function QuestionsStep({
  episodeId,
  setup,
  onBack,
  onDone,
}: {
  episodeId: string;
  setup: StorySetup;
  onBack: () => void;
  onDone: (setup: StorySetup, suggested: SetupSuggestions) => void;
}) {
  const [answers, setAnswers] = useState(() => answersOf(setup));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.saveEpisodeSetup(episodeId, answersPatch(answers));
      onDone(res.setup, res.suggested);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <StepHeader step={5} title="A few quick questions" subtitle="They shape the music, the look and, later, the story. Skip any you like." />

      <StoryAnswerFields value={answers} onChange={(patch) => setAnswers((a) => ({ ...a, ...patch }))} />

      {error && (
        <p className="text-sm text-red-200" role="alert">
          {error}
        </p>
      )}

      <ActionBar>
        <button onClick={onBack} className={secondaryBtn}>
          Back
        </button>
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving…' : 'Next'}
        </button>
      </ActionBar>
    </div>
  );
}

// ---- Voice input (the browser's own speech recognition, where available) ----

interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
}

function recognitionCtor(): (new () => Recognition) | null {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * "Speak instead": hidden when the browser has no speech recognition. Some
 * browsers send the audio to their own speech service to transcribe it, so
 * the button says so.
 */
function Dictation({ lang, onText, onStop }: { lang: string; onText: (t: string) => void; onStop?: () => void }) {
  const [Ctor] = useState(recognitionCtor);
  const [listening, setListening] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const rec = useRef<Recognition | null>(null);
  useEffect(() => () => rec.current?.stop(), []);
  if (!Ctor) return null;

  const toggle = () => {
    if (listening) {
      rec.current?.stop();
      return;
    }
    setProblem(null);
    const r = new Ctor();
    r.lang = lang;
    r.interimResults = false;
    r.continuous = true;
    r.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      if (last?.isFinal && last[0]?.transcript) onText(last[0].transcript.trim());
    };
    r.onerror = (e) => setProblem(e.error === 'not-allowed' ? 'Microphone access was blocked.' : 'Voice input stopped.');
    r.onend = () => {
      setListening(false);
      onStop?.();
    };
    rec.current = r;
    r.start();
    setListening(true);
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <button type="button" onClick={toggle} aria-pressed={listening} className={`${secondaryBtn} py-2 text-xs`}>
        {listening ? '■ Stop' : '🎙 Speak instead'}
      </button>
      <span className="text-xs text-white/35">
        {listening ? 'Listening…' : problem ?? 'Uses your browser’s speech service.'}
      </span>
    </div>
  );
}
